/**
 * Default-paths Traffic Distribution AI helpers (flow-scoped automationConfiguration).
 *
 * Voluum exposes a single set-level toggle here, not a per-path setting. We
 * read / write `flow.automationConfiguration` (Voluum UI counts it as "1 set").
 *
 * The legacy per-path TP keys (`__tpPathsDistributionAi`) are no longer used
 * for this control — they did not actually persist to Voluum.
 */

import type {
  VoluumCalculationMethod,
  VoluumFlowUpdate,
  VoluumPathUpdate,
} from '@trafficpulse/shared';

import {
  getSetAutoOptimized,
  getSetCalculationMethod,
  setSetAutoOptimized,
  setSetCalculationMethod,
} from './set-automation';

export type DefaultPathsAutoOptimizedUniform = 'allOn' | 'allOff' | 'mixed';

export function patchEachDefaultPath(
  draft: VoluumFlowUpdate,
  mutator: (p: VoluumPathUpdate) => void,
): void {
  draft.defaultPaths?.forEach(mutator);
}

export function defaultPathsOptimizationStats(draft: VoluumFlowUpdate): {
  total: number;
  enabled: number;
} {
  const total = draft.defaultPaths?.length ?? 0;
  if (total === 0) return { total: 0, enabled: 0 };
  return { total: 1, enabled: getSetAutoOptimized(draft) ? 1 : 0 };
}

/**
 * Since Voluum exposes a single flow-level toggle, the "mixed" state can no
 * longer arise from the wire. We still return the discriminated union so the
 * UI keeps a stable contract; only `allOn` / `allOff` are reachable now.
 */
export function defaultPathsAutoOptimizedUniform(
  draft: VoluumFlowUpdate,
): DefaultPathsAutoOptimizedUniform {
  if (!draft.defaultPaths || draft.defaultPaths.length === 0) return 'allOff';
  return getSetAutoOptimized(draft) ? 'allOn' : 'allOff';
}

export function defaultPathsCalculationMethodUniform(
  draft: VoluumFlowUpdate,
): VoluumCalculationMethod | null {
  return getSetCalculationMethod(draft);
}

export function setDefaultPathsAutoOptimized(
  draft: VoluumFlowUpdate,
  next: boolean,
): void {
  setSetAutoOptimized(draft, next);
}

export function setDefaultPathsCalculationMethod(
  draft: VoluumFlowUpdate,
  next: VoluumCalculationMethod,
): void {
  setSetCalculationMethod(draft, next);
}

/**
 * Aggregate counts of every "set" of paths on a flow:
 *  - 1 default-paths set
 *  - 1 per rule-based group
 *
 * Matches Voluum's own UI counter ("X/Y sets of paths optimized").
 */
export function allSetsOptimizationStats(draft: VoluumFlowUpdate): {
  total: number;
  enabled: number;
} {
  const hasDefault = (draft.defaultPaths?.length ?? 0) > 0;
  const groups = draft.conditionalPathsGroups ?? [];
  const total = (hasDefault ? 1 : 0) + groups.length;
  const enabled =
    (hasDefault && getSetAutoOptimized(draft) ? 1 : 0) +
    groups.filter((g) => getSetAutoOptimized(g)).length;
  return { total, enabled };
}

export type AllSetsUniform = 'allOn' | 'allOff' | 'mixed';

export function allSetsAutoOptimizedUniform(
  draft: VoluumFlowUpdate,
): AllSetsUniform {
  const { total, enabled } = allSetsOptimizationStats(draft);
  if (total === 0 || enabled === 0) return 'allOff';
  if (enabled === total) return 'allOn';
  return 'mixed';
}

/** Master toggle: cascades AI on/off to flow + every rule-based group. */
export function setAllSetsAutoOptimized(
  draft: VoluumFlowUpdate,
  next: boolean,
): void {
  setSetAutoOptimized(draft, next);
  draft.conditionalPathsGroups?.forEach((g) => setSetAutoOptimized(g, next));
}

/** Master calculation-method: applies the same method to flow + every group. */
export function setAllSetsCalculationMethod(
  draft: VoluumFlowUpdate,
  next: VoluumCalculationMethod,
): void {
  setSetCalculationMethod(draft, next);
  draft.conditionalPathsGroups?.forEach((g) => setSetCalculationMethod(g, next));
}
